
import Enumerable from "linq";
import { Component, Mixins, Watch } from "vue-property-decorator";
import { DomainRegistry } from "../../../../domain";
import { Department, DepartmentGroup } from "../../../../domain/department";
import { FormatReference } from "../../../../domain/format";
import {
  DocumentSearchPresetCondition,
  SearchConditionPresetKinds,
  DocumentSearchParameter,
  DocumentKind,
} from "../../../../domain/document";
import { StandardOption } from "../../../system/selectors/mixins";

@Component
export default class DocumentsFinderPresetMixin extends Mixins() {
  data(): any {
    return {
      isPresetLoading: false,
      presetConditions: [],
      preset: null,
      selectedPresetOption: null,
      sortedPresetKeys: [],
      selectedDepartmentGroup: null,
    };
  }
  isPresetLoading: boolean;
  presetConditions: DocumentSearchPresetCondition[];
  preset: DocumentSearchPresetCondition;
  selectedPresetOption: DocumentSearchPresetCondition;
  sortedPresetKeys: string[];
  selectedDepartmentGroup: DepartmentGroup;

  get maxDisplayPresetCount(): number {
    return 3;
  }

  get displaySearchPresetConditions(): DocumentSearchPresetCondition[] {
    return (this.sortedSearchPresetConditions.length < 1) ? [] : this.sortedSearchPresetConditions.slice(0, this.displayPresetCount);
  }

  get displayPresetCount(): number {
    return (this.presetConditions.length <= this.maxDisplayPresetCount)
      ? this.presetConditions.length : this.maxDisplayPresetCount - 1;
  }

  get departmentId(): string {
    return this.$store.getters.department
      ? this.$store.getters.department.id
      : null;
  }

  get isPresetDropDownEnable(): boolean {
    return (this.maxDisplayPresetCount < this.presetConditions.length);
  }

  get presetOptions(): StandardOption<DocumentSearchPresetCondition>[] {
    const dropDownConditions = this.isPresetDropDownEnable
      ? this.sortedSearchPresetConditions.slice(this.maxDisplayPresetCount - 1, this.presetConditions.length)
      : [];
    return Enumerable.from(dropDownConditions)
      .select(c => new StandardOption<DocumentSearchPresetCondition>(this.toPresetName(c), c))
      .toArray();
  }

  get shouldSortPresetByOrderSettings(): boolean {
    return this.sortedPresetKeys
      && (0 < this.sortedPresetKeys.length)
      && Enumerable.from(this.presetConditions ? this.presetConditions : []).any(s => this.sortedPresetKeys.some(k => k === s.key));
  }

  get sortedSearchPresetConditions(): DocumentSearchPresetCondition[] {
    return this.shouldSortPresetByOrderSettings
      ? this.createSortedSearchPresetConditions(this.presetConditions, this.sortedPresetKeys)
      : this.presetConditions ? Enumerable.from(this.presetConditions).orderBy(c => c.presetKind).thenBy(c => c.index).toArray() : [];
  }

  set sortedSearchPresetConditions(conditions: DocumentSearchPresetCondition[]) {
    this.sortedPresetKeys = Enumerable.from(conditions).select(c => c.key).toArray();
  }

  get selectedPreset(): DocumentSearchPresetCondition {
    return this.preset ? this.preset : new DocumentSearchPresetCondition();
  }

  set selectedPreset(source: DocumentSearchPresetCondition) {
    this.preset = source;
  }

  get condition(): DocumentSearchParameter {
    return this.selectedPreset.condition;
  }

  get presetPatientId(): string {
    return this.condition.patientId;
  }

  set presetPatientId(value: string) {
    this.condition.patientId = value;
  }

  get containsDeleted(): boolean {
    return this.condition.containsDeleted;
  }

  set containsDeleted(value: boolean) {
    this.condition.containsDeleted = value;
  }

  get containsTemporary(): boolean {
    return this.condition.containsTemporary;
  }

  set containsTemporary(value: boolean) {
    this.condition.containsTemporary = value;
  }

  get containsRegistered(): boolean {
    return this.condition.containsRegistered;
  }

  set containsRegistered(value: boolean) {
    this.condition.containsRegistered = value;
  }

  get loginUserOnly(): boolean {
    return this.condition.loginUserOnly;
  }

  set loginUserOnly(value: boolean) {
    this.condition.loginUserOnly = value;
  }

  get containsVirtualDocuments(): boolean {
    return this.condition.containsVirtualDocuments;
  }

  set containsVirtualDocuments(value: boolean) {
    this.condition.containsVirtualDocuments = value;
  }

  get isDurationDocument(): boolean {
    return this.condition.contentDateSearch;
  }

  set isDurationDocument(value: boolean) {
    this.condition.contentDateSearch = value;
  }

  get manageNumberText(): string {
    return this.condition.manageNumberText;
  }

  set manageNumberText(value: string) {
    this.condition.manageNumberText = value;
  }

  get formatName(): string {
    return this.condition.formatName;
  }

  set formatName(value: string) {
    this.condition.formatName = value;
  }

  get formatIds(): string {
    return this.condition.formatIds;
  }

  set formatIds(value: string) {
    this.condition.formatIds = value;
  }

  get loginUserId(): string {
    return this.condition.loginUserId;
  }

  set loginUserId(value: string) {
    this.condition.loginUserId = value;
  }

  get documentKindIds(): string[] {
    return this.condition.documentKindIds;
  }

  set documentKindIds(value: string[]) {
    this.condition.documentKindIds = value;
  }

  get departmentGroup(): DepartmentGroup {
    return this.selectedDepartmentGroup;
  }

  set departmentGroup(value: DepartmentGroup) {
    this.selectedDepartmentGroup = value;
    this.departmentGroupId = value
      ? value.id
      : "";
    this.presetDepartmentIds = this.toPresetDepartmentIds(value);
  }

  get departmentGroupId(): string {
    return this.condition.departmentGroupId;
  }

  set departmentGroupId(value: string) {
    this.condition.departmentGroupId = value;
  }

  get presetDepartmentIds(): string[] {
    return this.condition.departmentIds;
  }

  set presetDepartmentIds(value: string[]) {
    this.condition.departmentIds = value;
  }

  get presetDepartmentId(): string {
    return this.condition.departmentId;
  }

  set presetDepartmentId(value: string) {
    this.condition.departmentId = value;
  }

  get fromDate(): string {
    return this.condition.fromDate;
  }

  set fromDate(value: string) {
    this.condition.fromDate = value;
  }

  get toDate(): string {
    return this.condition.toDate;
  }

  set toDate(value: string) {
    this.condition.toDate = value;
  }

  get durationName(): string {
    return this.condition.durationName;
  }

  set durationName(value: string) {
    this.condition.durationName = value;
  }

  get updateUserCodes(): string[] {
    return this.condition.updateUserCodes;
  }

  set updateUserCodes(value: string[]) {
    this.condition.updateUserCodes = value;
  }

  isPresetFormat(format: FormatReference): boolean {
    const formatIds = this.condition.formatIds
      ? this.toNumberArray(this.split(this.condition.formatIds, ","))
      : [];
    const formatNames = this.condition.formatName
      ? this.split(this.condition.formatName, ",")
      : [];
    return formatIds.some(f => f === format.id) || formatNames.some(n => format.name.indexOf(n) >= 0);
  }

  isPresetButtonSelected(condition: DocumentSearchPresetCondition): boolean {
    // プリセットフィルターキーのプリセットはkeyが無いため、presetKind・name・indexで判定する
    return this.selectedPreset
      && this.selectedPreset.presetKind === condition.presetKind
      && this.selectedPreset.name === condition.name
      && this.selectedPreset.index === condition.index;
  }

  isPresetOptionCondition(condition: DocumentSearchPresetCondition): boolean {
    // presetOptionsにはkeyがあるプリセットしか無いのでkeyで判定する
    return this.isPresetDropDownEnable
      && Enumerable.from(this.presetOptions)
        .any(o => o.value && o.value.key === condition.key);
  }

  presetEqual(source: DocumentSearchPresetCondition, dest: DocumentSearchPresetCondition): boolean {
    // presetOptionsにはkeyがあるプリセットしか無いのでkeyで判定する
    return source && dest && (source.key === dest.key);
  }

  split(source: string, separator: string): string[] {
    return source.split(separator);
  }

  toNumberArray(source: string[]): number[] {
    return Enumerable.from(source).select(s => Number(s)).toArray();
  }

  toArrayOrEmpty<T>(source: Enumerable.IEnumerable<T>): T[] {
    return source
      ? source.toArray()
      : [];
  }

  toPresetName(preset: DocumentSearchPresetCondition): string {
    if (preset.name) { return preset.name; }
    switch (preset.presetKind) {
      case SearchConditionPresetKinds.Person: return "個）プリセット" + preset.index;
      case SearchConditionPresetKinds.Department: return "診）プリセット" + preset.index;
      case SearchConditionPresetKinds.All: return "全）プリセット" + preset.index;
      default: "";
    }
  }

  toDefaultSelected(condition: DocumentSearchPresetCondition): DocumentSearchPresetCondition {
    condition.isDefaultSelected = true;
    return condition;
  }

  createSortedSearchPresetConditions(presets: DocumentSearchPresetCondition[], sortedPresetKeys: string[]): DocumentSearchPresetCondition[] {
    if (!presets) { return []; }
    // 並び順
    // プリセットフィルターキーのプリセット + 並び順未登録の個人プリセットのindex降順 + 並び順登録済みプリセットのindex昇順 + 並び順未登録の診療科プリセットのindex降順 + 並び順未登録の全科プリセットのindex降順
    const presetFilterKeyPreset = Enumerable.from(presets).where(c => !c.key);
    const orderUnspecifiedConditions = Enumerable.from(presets)
      .where(c => c.key && sortedPresetKeys.every(k => k !== c.key))
      .orderByDescending(c => c.index)
      .groupBy(c => c.presetKind);
    const orderSpecifiedConditions = Enumerable.from(sortedPresetKeys)
      .select(k => Enumerable.from(presets).firstOrDefault(c => c.key === k))
      .where(c => !!c)
      .toArray();
    return this.toArrayOrEmpty(presetFilterKeyPreset)
      .concat(this.toArrayOrEmpty(orderUnspecifiedConditions.firstOrDefault(g => g.key() === SearchConditionPresetKinds.Person)))
      .concat(orderSpecifiedConditions)
      .concat(this.toArrayOrEmpty(orderUnspecifiedConditions.firstOrDefault(g => g.key() === SearchConditionPresetKinds.Department)))
      .concat(this.toArrayOrEmpty(orderUnspecifiedConditions.firstOrDefault(g => g.key() === SearchConditionPresetKinds.All)));
  }

  getPresetFilterBooleanValue(filterValue: boolean, defaultValue: boolean): boolean {
    return filterValue !== undefined
      ? (filterValue.toString() === "true")
      : defaultValue;
  }

  getPresetFilterStringValue(filterValue: string, defaultValue: string): string {
    return filterValue !== undefined
      ? filterValue
      : defaultValue;
  }

  toPresetDepartmentIds(departmentGroup: DepartmentGroup): string[] {
    return departmentGroup && departmentGroup.departments && departmentGroup.departments.length > 0
      ? Enumerable.from(departmentGroup.departments).select(d => d.id).toArray()
      : null;
  }

  getLatestPreset(presetKind: SearchConditionPresetKinds): DocumentSearchPresetCondition {
    return  Enumerable.from(this.presetConditions)
      .where(p => p.presetKind === presetKind)
      .orderByDescending(c => c.index).firstOrDefault();
  }

  async getPresetConditions(departmentId: string): Promise<DocumentSearchPresetCondition[]> {
    try {
      const repository = DomainRegistry.document.getDocumentRepository();
      return await repository.getPresetConditions(departmentId);
    } catch (e) {
      this.$error("プリセット取得失敗", "プリセットの取得に失敗しました。", e);
    }
  }

  async getPresetOrder(): Promise<string[]> {
    try {
      const repository = DomainRegistry.document.getDocumentRepository();
      return repository.getPresetOrder();
    } catch (e) {
      this.$error("プリセット並び順取得失敗", "文書一覧検索プリセットの並び順取得に失敗しました", e);
    }
  }

  @Watch("documentKinds")
  onDocumentKindsChanged(newValue: DocumentKind[]) {
    this.documentKindIds = newValue ? newValue.map(v => v.id) : [];
  }

  @Watch("department")
  onDepartmentChanged(newValue: Department) {
    this.presetDepartmentId = newValue ? newValue.id : null;
  }

  @Watch("formats")
  onFormatsChanged(newValue: FormatReference[]) {
    this.formatIds = newValue ? Enumerable.from(newValue).select(n => n.id.toString()).toArray().join(",") : null;
  }
}
